import { Info, Warning } from "@mui/icons-material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { createRef } from 'react';
import CopyToClipboardButton from './CopyToClipboardButton.js';
import { getDiscoveryEvidenceFromAnalysis, getWarningReasonForField } from './PathUtils.js';

const EVIDENCE_WARNING_FIELD_NAMES = ['verbatim', 'before_verbatim', 'after_verbatim', 'source'];

export default function Discovery({ caseInfo, fileNamesToFilePropertiesMap, displayedDocument, setDisplayedDocument, setSnackBarMessage }) {
    const tableRef = createRef();
    var documentKeysToDisplay = caseInfo !== null && 'documents' in caseInfo ?
        Object.keys(caseInfo['documents']).filter(docKey => fileNamesToFilePropertiesMap[caseInfo['documents'][docKey]['filename']]['selected']) :
        [];
    const isDiscoveryInProgress = caseInfo && Object.values(caseInfo['documents'] || {}).some(doc => doc['analysis_queue']?.includes('categorize') || doc['analysis_queue']?.includes('discovery_evidence') || doc['analysis_queue']?.includes('personal_injury_catchall'));

    var discoveryItems = documentKeysToDisplay.map(documentKey => {
        const evidence = getDiscoveryEvidenceFromAnalysis(caseInfo?.['documents']?.[documentKey]?.['analysis']);
        evidence.forEach((discoveryItem, i) => {
            discoveryItem['id'] = documentKey + i;
            discoveryItem['source_filekey'] = documentKey;
        });
        return evidence;
    })
        .filter(discoveryArr => discoveryArr.length > 0)
        .reduce((accumulator, discoveryArr) => accumulator.concat(discoveryArr), [])
        .filter(discoveryItem => isValidDiscoveryItem(discoveryItem));

    if (discoveryItems.length === 0) {
        return (
            <Box sx={{
                p: 3,
                textAlign: 'center',
                mt: 8,
                mb: 8
            }}>
                <Typography
                    variant="h6"
                    sx={{
                        color: 'var(--text-secondary)',
                        fontWeight: 400,
                        fontSize: '1.1rem',
                        lineHeight: 1.5
                    }}
                >
                    Potential evidence will be automatically extracted from uploaded discovery documents such as depositions and interrogatories.
                </Typography>
            </Box>
        )
    }

    var tableBodyRows = discoveryItems
        .map(discoveryItem => {
            const documentKey = discoveryItem.source_filekey;
            const docInfo = caseInfo.documents[documentKey];
            const filename = docInfo?.['filename'];
            const sourcePage = discoveryItem.source?.page || 1;

            return (
                <TableRow
                    key={discoveryItem['id']}
                    onClick={() => {
                        setDisplayedDocument({ 'key': discoveryItem['source_filekey'], 'page': sourcePage })
                    }}
                    className={discoveryItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                    sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            backgroundColor: 'var(--surface-color)',
                            transform: 'translateY(-1px)',
                            boxShadow: 'var(--shadow-subtle)'
                        },
                        '&:hover .source-view-icon': {
                            visibility: 'visible',
                            opacity: 0.7
                        }
                    }}
                >
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)',
                    }}>
                        {renderEvidenceWithReview(discoveryItem)}
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-secondary)',
                        borderBottom: '1px solid var(--border-color)',
                    }}>
                        {renderFieldWithReview(discoveryItem, 'reason')}
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-secondary)',
                        borderBottom: '1px solid var(--border-color)',
                        textAlign: 'center'
                    }}>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                                component="span"
                                title="Click to view source document"
                                sx={{
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    fontSize: '0.9rem',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': discoveryItem['source_filekey'],
                                        'page': sourcePage
                                    });
                                }}
                            >
                                {filename} {(discoveryItem.source?.page || discoveryItem.page) && `(page ${discoveryItem.source?.page || discoveryItem.page})`}{discoveryItem.source?.bates_number && `(${discoveryItem.source?.bates_number})`}
                            </Typography>
                            <IconButton
                                className="source-view-icon"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': discoveryItem['source_filekey'],
                                        'page': sourcePage
                                    });
                                }}
                                sx={{
                                    padding: '2px',
                                    visibility: 'hidden',
                                    opacity: 0,
                                    transition: 'opacity 0.2s, visibility 0.2s',
                                    '&:hover': {
                                        opacity: 1,
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                    }
                                }}
                                title="Click to view source document"
                            >
                                <VisibilityIcon />
                            </IconButton>
                        </Box>
                    </TableCell>
                </TableRow>
            );
        });

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {isDiscoveryInProgress && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/loading.gif"
                                alt="loading..."
                                title="Processing..."
                                style={{
                                    marginRight: "8px",
                                    height: "16px",
                                    verticalAlign: "middle"
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontStyle: 'italic',
                                    fontSize: '1.1rem',
                                    fontWeight: 500
                                }}
                            >
                                Discovery documents are being analyzed for evidence...
                            </Typography>
                        </Box>
                    )}
                </Box>
                <CopyToClipboardButton nodeRef={tableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Discovery" contentType="discovery" />
            </Box>
            <Table
                ref={tableRef}
                sx={{
                    minWidth: 650,
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)'
                }}
                aria-label="discovery table"
            >
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            minWidth: "150px"
                        }}>
                            Evidence
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Notes
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Source Document
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableBodyRows}
                </TableBody>
            </Table>
        </Box>
    );

    function isValidDiscoveryItem(discoveryItem) {
        return discoveryItem['verbatim'] || discoveryItem['source']?.verbatim;
    }

    function renderWarningRows(warnings, type) {
        if (!Array.isArray(warnings)) return [];
        const Icon = type === 'needs-review' ? Warning : Info;
        const color = type === 'needs-review' ? 'var(--error-color)' : 'var(--info-color)';
        return warnings
            .filter((w) => w?.reason)
            .map((w, idx) => (
                <Box key={`${type}-${w.field_name}-${idx}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                    <Icon sx={{ color, fontSize: '16px', mt: 0.5, flexShrink: 0 }} />
                    <Typography sx={{ color, fontSize: '12px', fontWeight: 500 }}>
                        {w.reason}
                    </Typography>
                </Box>
            ));
    }

    function renderEvidenceWithReview(discoveryItem) {
        const needsReview = discoveryItem.needs_review;
        const caveats = discoveryItem.caveats;

        const evidenceContent = (
            <Typography component='div' fontWeight={200}>
                "{discoveryItem.source?.before_verbatim || discoveryItem.before_verbatim}
                <Box fontWeight='fontWeightMedium' display='inline'>
                    {' ' + (discoveryItem.source?.verbatim || discoveryItem.verbatim) + ' '}
                </Box>
                {discoveryItem.source?.after_verbatim || discoveryItem.after_verbatim}"
            </Typography>
        );

        const warningMessages = [
            ...renderWarningRows(
                Array.isArray(needsReview)
                    ? needsReview.filter((w) => EVIDENCE_WARNING_FIELD_NAMES.includes(w?.field_name))
                    : null,
                'needs-review'
            ),
            ...renderWarningRows(
                Array.isArray(caveats)
                    ? caveats.filter((w) => EVIDENCE_WARNING_FIELD_NAMES.includes(w?.field_name))
                    : null,
                'caveats'
            ),
        ];

        if (warningMessages.length > 0) {
            return (
                <Box>
                    {warningMessages}
                    {evidenceContent}
                </Box>
            );
        }

        return evidenceContent;
    }

    function renderFieldWithReview(discoveryItem, fieldName, customValue = null) {
        const needsReviewReason = getWarningReasonForField(discoveryItem.needs_review, fieldName);
        const caveatsReason = getWarningReasonForField(discoveryItem.caveats, fieldName);
        const fieldValue = customValue !== null ? customValue : (discoveryItem[fieldName] || '');

        const warningMessages = [];

        if (needsReviewReason) {
            warningMessages.push(
                <Box key="needs-review" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                    <Warning sx={{ color: 'var(--error-color)', fontSize: '16px', mt: 0.5, flexShrink: 0 }} />
                    <Typography sx={{ color: 'var(--error-color)', fontSize: '12px', fontWeight: 500 }}>
                        {needsReviewReason}
                    </Typography>
                </Box>
            );
        }

        if (caveatsReason) {
            warningMessages.push(
                <Box key="caveats" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                    <Info sx={{ color: 'var(--info-color)', fontSize: '16px', mt: 0.5, flexShrink: 0 }} />
                    <Typography sx={{ color: 'var(--info-color)', fontSize: '12px', fontWeight: 500 }}>
                        {caveatsReason}
                    </Typography>
                </Box>
            );
        }

        if (warningMessages.length > 0) {
            return (
                <Box>
                    {warningMessages}
                    <Typography sx={{ color: 'var(--text-primary)' }}>
                        {fieldValue}
                    </Typography>
                </Box>
            );
        }

        return fieldValue;
    }
}
